import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, FileText, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'

import { eventsApi, type EventInvoice } from '@/services/events'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Empty } from '@/components/ui/empty'
import { cn, formatCurrency } from '@/lib/utils'
import { getErrorMessage } from '@/lib/api'

type EventInvoicesPanelProps = {
  jobId: string | number
}

function statusBadgeClass(status: string) {
  switch (status?.toLowerCase()) {
    case 'paid':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200'
    case 'issued':
      return 'bg-blue-50 text-blue-700 border-blue-200'
    case 'pending':
    case 'draft':
      return 'bg-amber-50 text-amber-700 border-amber-200'
    case 'overdue':
      return 'bg-red-50 text-red-700 border-red-200'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

function InvoiceRow({
  invoice,
  jobId,
  onUpdated,
}: {
  invoice: EventInvoice
  jobId: string | number
  onUpdated: () => void
}) {
  const finalizeMutation = useMutation({
    mutationFn: () => eventsApi.invoices.finalize(jobId, invoice.id),
    onSuccess: () => {
      toast.success('Invoice finalized')
      onUpdated()
    },
    onError: (error) => {
      toast.error(getErrorMessage(error) ?? 'Failed to finalize invoice')
    },
  })

  const markPaidMutation = useMutation({
    mutationFn: () => eventsApi.invoices.markPaid(jobId, invoice.id),
    onSuccess: () => {
      toast.success('Invoice marked as paid')
      onUpdated()
    },
    onError: (error) => {
      toast.error(getErrorMessage(error) ?? 'Failed to mark invoice as paid')
    },
  })

  const isPending = finalizeMutation.isPending || markPaidMutation.isPending
  const status = invoice.status?.toLowerCase()
  const canFinalize = status === 'draft' || status === 'pending'
  const canMarkPaid = status === 'issued'

  return (
    <div className="rounded-xl border border-border/60 p-3">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-medium text-foreground">
              {invoice.worker_name ?? `Worker #${invoice.worker_id}`}
            </p>
            <Badge
              variant="outline"
              className={cn('text-xs capitalize shrink-0', statusBadgeClass(invoice.status))}
            >
              {invoice.status}
            </Badge>
          </div>
          <div className="mt-0.5 flex items-center gap-3 text-xs text-muted-foreground">
            <span>Invoice #{invoice.id}</span>
            <span>{formatCurrency(invoice.amount)}</span>
            {invoice.days_outstanding != null && invoice.days_outstanding > 0 ? (
              <span className="text-red-600">{invoice.days_outstanding}d outstanding</span>
            ) : null}
          </div>
          {invoice.paid_amount != null ? (
            <p className="mt-0.5 text-xs text-emerald-600">
              Paid: {formatCurrency(invoice.paid_amount)}
            </p>
          ) : null}
        </div>

        <div className="flex shrink-0 gap-2">
          {canFinalize ? (
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs"
              disabled={isPending}
              onClick={() => finalizeMutation.mutate()}
            >
              {finalizeMutation.isPending ? (
                <RefreshCw className="mr-1 h-3 w-3 animate-spin" />
              ) : (
                <FileText className="mr-1 h-3 w-3" />
              )}
              Finalize
            </Button>
          ) : null}
          {canMarkPaid ? (
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
              disabled={isPending}
              onClick={() => markPaidMutation.mutate()}
            >
              {markPaidMutation.isPending ? (
                <RefreshCw className="mr-1 h-3 w-3 animate-spin" />
              ) : (
                <CheckCircle2 className="mr-1 h-3 w-3" />
              )}
              Mark Paid
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export function EventInvoicesPanel({ jobId }: EventInvoicesPanelProps) {
  const queryClient = useQueryClient()

  const invoicesQuery = useQuery({
    queryKey: ['event-invoices', jobId],
    queryFn: () => eventsApi.invoices.list(jobId),
    staleTime: 30_000,
  })

  const summaryQuery = useQuery({
    queryKey: ['event-invoice-summary', jobId],
    queryFn: () => eventsApi.invoices.summary(jobId),
    staleTime: 30_000,
  })

  const handleUpdated = () => {
    queryClient.invalidateQueries({ queryKey: ['event-invoices', jobId] })
    queryClient.invalidateQueries({ queryKey: ['event-invoice-summary', jobId] })
    queryClient.invalidateQueries({ queryKey: ['event-payment-snapshot', jobId] })
  }

  const invoices = invoicesQuery.data ?? []
  const summary = summaryQuery.data

  return (
    <div className="space-y-4">
      {/* Summary */}
      {summary ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-xl border border-border/60 p-3 text-center">
            <p className="text-xs text-muted-foreground">Total</p>
            <p className="mt-1 text-lg font-semibold">{summary.total}</p>
          </div>
          <div className="rounded-xl border border-border/60 p-3 text-center">
            <p className="text-xs text-muted-foreground">Paid</p>
            <p className="mt-1 text-lg font-semibold text-emerald-600">{summary.paid_count}</p>
            <p className="text-xs text-muted-foreground">{formatCurrency(summary.paid_amount)}</p>
          </div>
          <div className="rounded-xl border border-border/60 p-3 text-center">
            <p className="text-xs text-muted-foreground">Pending</p>
            <p className="mt-1 text-lg font-semibold text-amber-600">{summary.pending_count}</p>
            <p className="text-xs text-muted-foreground">{formatCurrency(summary.pending_amount)}</p>
          </div>
          <div className="rounded-xl border border-border/60 p-3 text-center">
            <p className="text-xs text-muted-foreground">Overdue</p>
            <p className="mt-1 text-lg font-semibold text-red-600">{summary.overdue_count}</p>
          </div>
        </div>
      ) : summaryQuery.isLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
        </div>
      ) : null}

      {/* Invoice list */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Invoices</CardTitle>
          <CardDescription>Manage individual worker invoices</CardDescription>
        </CardHeader>
        <CardContent>
          {invoicesQuery.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
            </div>
          ) : !invoices.length ? (
            <Empty className="py-8">
              <FileText className="mb-3 h-8 w-8 text-muted-foreground/60" />
              <p className="text-sm font-medium text-foreground">No invoices yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Use "Generate Invoices" from the Payment tab to create invoices for all workers.
              </p>
            </Empty>
          ) : (
            <div className="space-y-2">
              {invoices.map((invoice) => (
                <InvoiceRow
                  key={String(invoice.id)}
                  invoice={invoice}
                  jobId={jobId}
                  onUpdated={handleUpdated}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
